import { Link } from "react-router-dom";
import {
  AlertTriangle, Globe2, ArrowRight, DollarSign, ShieldCheck,
  Calendar, ExternalLink,
} from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { Button } from "@/components/ui/button";


// Non-EU/EEA annual undergraduate tuition sourced from each country's
// national student portal (studyinsweden.se, optagelse.dk,
// utdanning.no, opintopolku.fi, studyiniceland.is) — checked
// 2026-02-27 for AY 2024-25. EU / EEA / Swiss / UK families remain
// tuition-free at ALL five Nordic states.
const NORDIC_TUITION_TABLE = [
  {
    code: "SE", flag: "🇸🇪", country: "Sweden",
    fees_eu: 0,
    fees_intl_min: 8500, fees_intl_max: 16000,
    changed_year: null,
    note: "Application via the national universityadmissions.se " +
          "portal — hard deadline 15 January for autumn intake.",
  },
  {
    code: "DK", flag: "🇩🇰", country: "Denmark",
    fees_eu: 0,
    fees_intl_min: 8000, fees_intl_max: 19000,
    changed_year: null,
    note: "Split admissions via optagelse.dk — Quota 1 (grades " +
          "only, 5 July deadline) and Quota 2 (grades + motivation, " +
          "15 March deadline).",
  },
  {
    code: "NO", flag: "🇳🇴", country: "Norway",
    fees_eu: 0,
    fees_intl_min: 24000, fees_intl_max: 56000,
    changed_year: 2023,
    changed_headline: "Non-EU tuition introduced Aug 2023.",
    note: "First cohort paid ~NOK 250k-400k/yr (~USD 24k-38k). " +
          "Fees rose sharply in 2024-25 — NTNU engineering now " +
          "quotes NOK 400k-600k. Most legacy Norway study guides " +
          "still list Norway as free.",
  },
  {
    code: "FI", flag: "🇫🇮", country: "Finland",
    fees_eu: 0,
    fees_intl_min: 8000, fees_intl_max: 18000,
    changed_year: 2017,
    changed_headline: "Non-EU tuition introduced Aug 2017.",
    note: "Aalto, Helsinki and Turku charge EUR 8k-15k/yr for " +
          "English-taught bachelors. Finnish-taught programmes " +
          "remain tuition-free even for non-EU applicants (but " +
          "require Finnish B2).",
  },
  {
    code: "IS", flag: "🇮🇸", country: "Iceland",
    fees_eu: 0,
    fees_intl_min: 0, fees_intl_max: 0,
    changed_year: null,
    note: "University of Iceland charges a nominal registration " +
          "fee (~USD 550/yr) — the only Nordic state that remains " +
          "entirely tuition-free for non-EU citizens at public " +
          "universities.",
  },
];

const usdFormat = (n) => {
  if (n === 0) return "Free";
  return `$${n.toLocaleString()}`;
};


export default function NordicTuitionReality() {
  return (
    <div
      data-testid="nordic-tuition-reality"
      className="mx-auto max-w-6xl px-6 py-16 lg:py-24"
    >
      <SEOHead
        title="Nordic Tuition Reality — the honest 2024-25 fee picture (SE · DK · NO · FI · IS)"
        description={
          "Norway introduced non-EU/EEA tuition in Aug 2023 — most " +
          "study guides still list Norway as free. WhichUniversityAdvisor " +
          "publishes the honest 2024-25 non-EU fee ranges for all five " +
          "Nordic states, verified against each country's national student " +
          "portal."
        }
      />

      {/* HERO */}
      <section
        data-testid="nordic-tuition-hero"
        className="grid md:grid-cols-3 gap-10 items-start"
      >
        <div className="md:col-span-2">
          <div className="label-uppercase text-amber-700 inline-flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> Legacy guides are wrong
          </div>
          <h1 className="font-editorial text-4xl sm:text-5xl lg:text-6xl mt-3 leading-tight tracking-tight">
            Norway isn't free anymore. <em>Neither is Finland.</em>
          </h1>
          <p className="mt-6 text-stone-700 max-w-2xl leading-relaxed text-lg">
            Sweden charged non-EU/EEA tuition from <b>2011</b>.
            Finland followed in <b>Aug 2017</b>. Norway — the last
            Nordic holdout — introduced non-EU fees in <b>Aug 2023</b>.
            Only <b>Iceland</b> remains tuition-free. Most legacy
            study guides haven't been updated. This page has been —
            verified 2026-02-27 against each country's own national
            student portal.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <span className="inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 text-emerald-900 rounded-full px-3 py-1.5">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span className="font-underground-sc tracking-wider">
                Verified 2026-02-27 · national portals
              </span>
            </span>
            <span className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Globe2 className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">
                5 Nordic states · AY 2024-25
              </span>
            </span>
            <span className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <DollarSign className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">
                Non-EU annual UG fees, USD
              </span>
            </span>
          </div>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/flagships#nordic-trio">
              <Button
                data-testid="nordic-tuition-cta-flagships"
                size="lg"
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md"
              >
                See the Nordic Trio flagship pack
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/advanced?country=Norway,Sweden,Denmark,Finland,Iceland">
              <Button
                data-testid="nordic-tuition-cta-search"
                variant="outline"
                size="lg"
                className="rounded-md"
              >
                Search all Nordic bachelors
              </Button>
            </Link>
          </div>
        </div>

        <aside
          data-testid="nordic-tuition-hero-callout"
          className="rounded-lg border-2 border-amber-400 bg-amber-50 p-6"
        >
          <div className="label-uppercase !text-[10px] text-amber-800 inline-flex items-center gap-1">
            <Calendar className="w-3 h-3" /> The single biggest change
          </div>
          <div className="font-editorial text-4xl mt-2 text-amber-900 leading-tight">
            Aug 2023
          </div>
          <div className="text-sm text-amber-900/90 mt-2 leading-relaxed">
            Norway abolished free non-EU/EEA tuition. The first
            cohort (Autumn-23) paid roughly{" "}
            <b>NOK 250,000-400,000/yr</b>. AY 2024-25 top-of-band at
            NTNU engineering:
          </div>
          <div className="font-chip text-2xl text-amber-900 mt-3">
            USD 38,000-56,000
          </div>
          <div className="text-xs text-amber-800/80 mt-4">
            Note: EU / EEA / Swiss / UK / Faroese / Greenlandic
            students continue to pay <b>zero</b> tuition across all
            five Nordic states. The Norway change affects only
            non-EU citizens.
          </div>
        </aside>
      </section>

      {/* TABLE */}
      <section
        data-testid="nordic-tuition-table"
        className="mt-20"
      >
        <div className="label-uppercase text-stone-500">
          The 2024-25 non-EU/EEA fee picture
        </div>
        <h2 className="font-editorial text-3xl mt-2 leading-tight">
          Fees you'll actually pay, in USD.
        </h2>
        <p className="mt-3 text-stone-700 max-w-3xl">
          EU / EEA / Swiss / UK families continue to pay zero across
          all five states. The columns below apply to <b>non-EU</b>{" "}
          citizens only. Ranges reflect subject-mix — STEM &
          engineering sit at the top, humanities & social science at
          the bottom.
        </p>

        <div className="mt-8 rounded-lg border border-stone-300 overflow-hidden bg-white">
          <table className="w-full text-sm">
            <thead className="bg-stone-100 text-stone-700 text-left uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3">Country</th>
                <th className="px-4 py-3">EU/EEA/UK fee</th>
                <th className="px-4 py-3">Non-EU fee (annual)</th>
                <th className="px-4 py-3">When it changed</th>
                <th className="px-4 py-3">Application quirk</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-200">
              {NORDIC_TUITION_TABLE.map((row) => (
                <tr
                  key={row.code}
                  data-testid={`nordic-tuition-row-${row.code.toLowerCase()}`}
                  className="hover:bg-stone-50"
                >
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-2xl mr-2">{row.flag}</span>
                    <span className="font-semibold">{row.country}</span>
                  </td>
                  <td className="px-4 py-4 font-mono text-emerald-700">
                    Free
                  </td>
                  <td className="px-4 py-4 font-mono">
                    {row.fees_intl_min === 0 && row.fees_intl_max === 0
                      ? <span className="text-emerald-700">Free (nominal ~$550 reg fee)</span>
                      : <>
                          {usdFormat(row.fees_intl_min)}
                          <span className="text-stone-500 mx-1.5">–</span>
                          {usdFormat(row.fees_intl_max)}
                        </>}
                  </td>
                  <td className="px-4 py-4">
                    {row.changed_year ? (
                      <span className="inline-flex items-center gap-1.5 bg-amber-100 text-amber-900 rounded-full px-2 py-0.5 text-xs font-mono">
                        <AlertTriangle className="w-3 h-3" />
                        {row.changed_year}
                      </span>
                    ) : (
                      <span className="text-stone-500 text-xs">
                        No recent change
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-stone-700 text-xs leading-relaxed max-w-md">
                    {row.note}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* THE NORWAY STORY */}
      <section
        data-testid="norway-story"
        className="mt-20 grid md:grid-cols-3 gap-10"
      >
        <div>
          <div className="label-uppercase text-stone-500">The Norway story</div>
          <h2 className="font-editorial text-3xl mt-2 leading-tight">
            The change most families miss.
          </h2>
        </div>
        <div className="md:col-span-2 space-y-6 text-stone-800 leading-relaxed">
          <p>
            Norway was, until 2023, one of the world's very few
            fully tuition-free destinations for non-EU citizens —
            an outlier alongside Germany, Iceland, and a handful of
            others. The <em>Stortinget</em> (Norwegian parliament)
            voted in Dec-2022 to abolish that policy from Autumn-23.
            The first cohort of paying international students
            enrolled in <b>Aug 2023</b>. Programme-level fees are
            set by each institution, with a floor of{" "}
            <b>NOK 130,000/yr</b> for humanities and a ceiling above{" "}
            <b>NOK 600,000/yr</b> for medicine and integrated MEng
            (Sivilingeniør) tracks at NTNU and UiO.
          </p>
          <p>
            The consequence: legacy study-abroad blogs, YouTube
            explainers, and even some university-partner sites{" "}
            <em>still</em> present Norway as free. Families anchor
            on that outdated fact and only discover the true bill on
            the offer letter. WhichUniversityAdvisor's ingest
            pipeline stamps the{" "}
            <span className="font-mono text-xs bg-stone-100 rounded px-1.5 py-0.5">
              no_tuition_new_2023
            </span>{" "}
            warning on every Norwegian bachelor record so the
            <Link
              to="/advanced?country=Norway"
              className="text-emerald-700 hover:text-emerald-800 underline mx-1"
            >
              Advanced Search
            </Link>
            surfaces this fact before the shortlist is built.
          </p>
          <p className="text-sm text-stone-600">
            Norwegian citizens, EU / EEA / Swiss / UK / Faroese /
            Greenlandic citizens, and existing exchange-agreement
            students are exempt. Refugees with residence permits
            are exempt. PhD students remain fully funded and paid a
            stipend — the change affects bachelor's and master's
            fee-paying international entrants only.
          </p>
        </div>
      </section>

      {/* WHY IT MATTERS FOR THE SHORTLIST */}
      <section className="mt-20 rounded-lg border border-stone-300 bg-[#F3F1EC] p-10">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <div>
            <div className="label-uppercase text-stone-500">
              How WUA models this
            </div>
            <h2 className="font-editorial text-2xl mt-2 leading-snug">
              Every Norwegian record carries the fee reality on the card.
            </h2>
            <p className="mt-4 text-stone-700 text-sm leading-relaxed">
              Nordic anchor programmes in the flagship pack ship
              with three explicit disclosures:
            </p>
            <ul className="mt-3 space-y-2 text-sm text-stone-800">
              <li>
                <span className="font-mono text-xs bg-white border border-stone-300 rounded px-1.5 py-0.5 mr-2">
                  eu_free_fees
                </span>
                EU/EEA/Swiss/UK vs non-EU split, per record.
              </li>
              <li>
                <span className="font-mono text-xs bg-white border border-stone-300 rounded px-1.5 py-0.5 mr-2">
                  no_tuition_new_2023
                </span>
                Norway's Aug-2023 policy shift, on every NO record.
              </li>
              <li>
                <span className="font-mono text-xs bg-white border border-stone-300 rounded px-1.5 py-0.5 mr-2">
                  no_psw_12mo
                </span>
                12-month post-study permit rules, honest & current.
              </li>
            </ul>
          </div>
          <div>
            <div className="label-uppercase text-stone-500">Read the sources</div>
            <ul className="mt-3 space-y-3 text-sm">
              <li>
                <a
                  data-testid="nordic-source-diku"
                  href="https://hkdir.no/en/international-student-mobility/tuition-fees-for-students-from-outside-the-eu-eea-switzerland"
                  target="_blank" rel="noreferrer"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  HK-Dir · Norway non-EU tuition policy
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  data-testid="nordic-source-uas"
                  href="https://www.universityadmissions.se/en/all-you-need-to-know/tuition-fees/tuition-fees-application-fee-and-scholarships/"
                  target="_blank" rel="noreferrer"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  universityadmissions.se · Sweden tuition & fees
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  data-testid="nordic-source-optagelse"
                  href="https://ufm.dk/en/education/higher-education/tuition-fees-and-scholarships/tuition-fees"
                  target="_blank" rel="noreferrer"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  Danish Ministry · Denmark tuition & scholarships
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  data-testid="nordic-source-opintopolku"
                  href="https://www.studyinfo.fi/wp2/en/higher-education/tuition-fees-and-scholarships-for-international-students/"
                  target="_blank" rel="noreferrer"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  studyinfo.fi · Finland tuition & scholarships
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li>
                <a
                  data-testid="nordic-source-iceland"
                  href="https://english.hi.is/university/tuition_fees"
                  target="_blank" rel="noreferrer"
                  className="text-emerald-700 hover:text-emerald-800 inline-flex items-center gap-1.5"
                >
                  University of Iceland · registration fee
                  <ExternalLink className="w-3 h-3" />
                </a>
              </li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mt-16 text-center">
        <p className="text-stone-600 text-sm">
          Ready to build a Nordic shortlist with the honest numbers baked in?
        </p>
        <div className="mt-4 flex flex-wrap justify-center gap-3">
          <Link to="/flagships#nordic-trio">
            <Button
              data-testid="nordic-tuition-cta-bottom"
              size="lg"
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md"
            >
              See the Nordic Trio flagship pack
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/nordic-flagship-corridor">
            <Button
              data-testid="nordic-tuition-cta-corridor"
              variant="outline"
              size="lg"
              className="rounded-md"
            >
              Walk the 12-record Nordic Corridor
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
